import React, { Fragment, useState } from "react";
import { Link } from "react-router-dom";
import { Popover, Transition, Dialog } from "@headlessui/react";
import {
    PlusIcon,
    ExclamationTriangleIcon,
    XCircleIcon,
    CheckCircleIcon,
} from "@heroicons/react/24/outline";
import {
    MagnifyingGlassIcon,
    ChevronDownIcon,
} from "@heroicons/react/24/solid";
import { useSystemConfig } from "../context/systemConfigContext";
import SidebarToggleButton from "./layout/SidebarToggleButton";

export default function Header(props) {
    function classNames(...classes) {
        return classes.filter(Boolean).join(" ");
    }

    const [value, setValue] = useState(props.value);
    const { systemStatus } = useSystemConfig();

    const handleKeyDown = (e) => {
        if (e.code === "Enter") {
            props.setSearch(e.target.value);
        }
    };

    const getUsageLevel = () => {
        if (!systemStatus || !systemStatus.maxMonthlyConsultations) return null;

        const percentage =
            (systemStatus.currentMonthConsultations /
                systemStatus.maxMonthlyConsultations) *
            100;

        if (percentage >= 96) {
            return {
                level: "critical",
                color: "red",
                bgClass: "bg-red-100 dark:bg-red-900",
                textClass: "text-red-800 dark:text-red-200",
                iconClass: "text-red-600 dark:text-red-300",
                icon: XCircleIcon,
                message: `Límite alcanzado (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
            };
        } else if (percentage >= 81) {
            return {
                level: "warning-high",
                color: "orange",
                bgClass: "bg-orange-100 dark:bg-orange-900",
                textClass: "text-orange-800 dark:text-orange-200",
                iconClass: "text-orange-600 dark:text-orange-300",
                icon: ExclamationTriangleIcon,
                message: `Muy cerca del límite (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
            };
        } else if (percentage >= 61) {
            return {
                level: "warning",
                color: "yellow",
                bgClass: "bg-yellow-100 dark:bg-yellow-900",
                textClass: "text-yellow-800 dark:text-yellow-200",
                iconClass: "text-yellow-600 dark:text-yellow-300",
                icon: ExclamationTriangleIcon,
                message: `Acercándose al límite (${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations})`,
            };
        } else if (percentage > 0) {
            return {
                level: "normal",
                color: "green",
                bgClass: "bg-green-100 dark:bg-green-900",
                textClass: "text-green-800 dark:text-green-200",
                iconClass: "text-green-600 dark:text-green-300",
                icon: CheckCircleIcon,
                message: `Consultas: ${systemStatus.currentMonthConsultations}/${systemStatus.maxMonthlyConsultations}`,
            };
        }

        return null;
    };

    const renderSystemStatusIndicator = () => {
        if (!systemStatus) return null;

        if (!systemStatus.systemEnabled) {
            return (
                <div className="flex items-center px-3 py-1 mr-2 bg-red-100 dark:bg-red-900 rounded-md">
                    <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-300 mr-2" />
                    <span className="text-sm font-medium text-red-800 dark:text-red-200">
                        Sistema Deshabilitado
                    </span>
                </div>
            );
        }

        const usageLevel = getUsageLevel();

        if (!usageLevel) return null;

        const Icon = usageLevel.icon;

        return (
            <div
                className={`flex items-center px-3 py-1 mr-2 ${usageLevel.bgClass} rounded-md`}
            >
                <Icon className={`h-5 w-5 ${usageLevel.iconClass} mr-2`} />
                <span className={`text-sm font-medium ${usageLevel.textClass}`}>
                    {usageLevel.message}
                </span>
            </div>
        );
    };

    return (
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
            <div className="flex min-h-[72px] items-center gap-3 px-4 py-3 sm:px-6 lg:px-6">
                <div className="flex items-center md:hidden">
                    <SidebarToggleButton onClick={() => props.setSidebarOpen(true)} />
                </div>
                <div className="flex-1 flex w-full items-center justify-start">
                    {props.search ? (
                        <div className="w-full">
                            <label htmlFor="search" className="sr-only">
                                Search
                            </label>
                            <div className="flex min-w-0 flex-1 items-center rounded-xl border border-slate-200 bg-slate-50 px-3 text-slate-700 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200">
                                <MagnifyingGlassIcon
                                    className="h-5 w-5 shrink-0 text-primary"
                                    aria-hidden="true"
                                />
                                <input
                                    id="search"
                                    name="search"
                                    onKeyDown={(event) => handleKeyDown(event)}
                                    onChange={(event) =>
                                        setValue(event.target.value)
                                    }
                                    value={value}
                                    className="w-full border-none bg-transparent px-3 py-2.5 text-sm focus:border-transparent focus:outline-none focus:ring-0"
                                    placeholder={props.searchPlaceholder}
                                    type="search"
                                />
                            </div>
                        </div>
                    ) : null}
                </div>
                <div className="flex items-center gap-3">
                    {renderSystemStatusIndicator()}
                    {props.buttonText ? (
                        <Link
                            to={props.href}
                            className="hidden items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-white shadow-sm sm:inline-flex"
                        >
                            <PlusIcon
                                className="h-5 w-5"
                                aria-hidden="true"
                            />
                            {props.buttonText}
                        </Link>
                    ) : null}
                </div>
            </div>
        </div>
    );
}
